<template>
  <Badge
    :class="{ 'cursor-pointer': !disabled }"
    :color="color ?? colorInjected"
    @click.stop="badgeClicked"
  >
    <span class="text-monospace">{{ idText }}</span>
  </Badge>
</template>

<script setup lang="ts">
import { computed, inject, type PropType } from "vue";
import { useTranslation } from "@/language/translation";
import {
  BadgeColorInjectionKey,
  DisabledStateInjectionKey,
} from "../event-action/issue/IssueTypes";
import Badge from "./BadgeComponent.vue";

const props = defineProps({
  id: {
    type: [Number, String] as PropType<number | string>,
    required: true,
  },
  prefix: {
    type: String,
    required: true,
  },
  color: {
    type: String,
    default: null,
  },
});
const emit = defineEmits(["click"]);
const { t } = useTranslation();
const colorInjected = inject(BadgeColorInjectionKey, "");
const disabled = inject(DisabledStateInjectionKey, false);

const idText = computed(() => {
  if (typeof props.id === "string") {
    return props.id;
  }

  if (props.id === -1) {
    return `${props.prefix} - ${t("global.new")}`;
  }

  return `${props.prefix}${props.id}`;
});

async function badgeClicked(evt: MouseEvent) {
  if (evt.ctrlKey || evt.metaKey) {
    try {
      await navigator.clipboard.writeText(
        typeof props.id === "string" ? props.id : `${props.prefix}${props.id}`,
      );
      window.$toast?.({
        severity: "info",
        detail: t("components.badges.IDBadge.copyToClipboard").toString(),
        summary: t("global.successful").toString(),
      });
    } catch {
      // DO nothing, can be triggered, when chrome is not focused on mac
    }
  } else {
    emit("click");
  }
}
</script>
